/* The SPA's one global stylesheet. Everything else is styled inline
 * (src/lib/styles.ts); only rules that inline styles cannot express live
 * here — pseudo-classes, pseudo-elements, and the theme palettes. Built by
 * Vite into a hashed .css asset served from our origin, so it complies with
 * the production CSP (style-src 'self'; no inline <style>).
 */

/* ==========================================================================
 * #399 — theme palettes.
 *
 * This file, not theme.ts, is the SOURCE OF TRUTH for every colour value.
 * src/lib/theme.ts's `C` maps each token name to `var(--c-<token>)`; the
 * inline style objects reference those, so switching `data-theme` on <html>
 * repaints the whole app with no re-render and no second style object.
 *
 * The CSS-only default is deliberate. Theme "system" needs no JavaScript at
 * all: the media query below carries it, so a dark-mode user gets the dark
 * palette painted with the first stylesheet rather than after the bundle
 * evaluates. JS only stamps data-theme for an EXPLICIT light/dark choice,
 * which is why the dark block is written twice: once behind
 * prefers-color-scheme for "system", once behind the attribute so an explicit
 * choice wins in both directions. theme.contrast.test.ts asserts the two dark
 * blocks are identical, and checks every text/background pair in BOTH
 * palettes against WCAG AA.
 *
 * One flash remains, disclosed rather than fixed: a user whose EXPLICIT choice
 * disagrees with their OS setting sees the OS palette until the bundle
 * evaluates. The standard fix — a tiny render-blocking <script src> in <head>
 * — IS available to us: `script-src 'self'` permits a same-origin external
 * script and blocks only an inline one, which is a different thing. It is
 * simply not worth a render-blocking request on every page load to remove a
 * sub-paint flash that affects only the minority who overrode their OS.
 *
 * H-5 still holds in both palettes: `--c-gold` is a background/border colour
 * only (1.67:1 on white); text uses `--c-gold-text`. Because the button
 * label colours cannot be a constant across themes (white on the light
 * theme's dark red, near-black on the dark theme's light red), they are
 * their own tokens: --c-on-danger / --c-on-success.
 * ========================================================================== */

:root {
  --c-bg: #f8f9fa;
  --c-bg-card: #FFFFFF;
  --c-bg-card-hover: #fafafa;
  --c-bg-input: #f8f9fa;
  --c-bg-sidebar: #0a0a0a;
  --c-bg-sidebar-hover: #1a1a1a;

  --c-border: #e9ecef;
  --c-border-focus: #FBBF24;

  --c-gold: #FBBF24;
  --c-gold-text: #92600A;
  --c-gold-light: #FCD34D;
  --c-gold-dim: #D4A017;
  --c-gold-bg: rgba(251, 191, 36, 0.08);

  --c-text: #111111;
  --c-text-secondary: #444444;
  --c-text-muted: #666666;
  --c-text-dim: #666666;

  --c-side-text: #999999;
  --c-side-text-dim: #8a8a8a;
  --c-side-text-active: #FBBF24;

  --c-success: #15803d;
  --c-success-bg: rgba(34, 197, 94, 0.08);
  --c-success-light: #f0fdf4;
  --c-danger: #b91c1c;
  --c-danger-bg: rgba(239, 68, 68, 0.06);
  --c-danger-light: #fef2f2;
  --c-warn: #b45309;
  --c-warn-bg: rgba(245, 158, 11, 0.07);
  --c-warn-light: #fffbeb;
  --c-info: #1d4ed8;
  --c-info-bg: rgba(59, 130, 246, 0.06);
  --c-info-light: #eff6ff;

  /* StatusBadge's two non-status families: FINAL_DETERMINATION (special) and
   * CLOSED / EXPIRED / UNCHANGED (neutral). They were literals in the
   * component; a literal has no dark counterpart, so they are tokens now. */
  --c-special: #6d28d9;
  --c-special-bg: rgba(128, 90, 213, 0.08);
  --c-neutral: #4a5568;
  --c-neutral-bg: rgba(113, 128, 150, 0.08);

  --c-on-danger: #FFFFFF;
  --c-on-success: #FFFFFF;

  --c-white: #FFFFFF;
  --c-black: #111111;

  /* Table density (#399). Comfortable is the default and reproduces the
   * pre-#399 padding exactly; data-density="compact" tightens it. Cell
   * padding cannot be a theme token because it is not a colour, but it is
   * the same mechanism: one attribute on <html>, no re-render. */
  --density-cell-pad-y: 10px;
  --density-cell-pad-x: 12px;
  --density-row-font-size: 13px;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --c-bg: #101418;
    --c-bg-card: #181d23;
    --c-bg-card-hover: #1f252c;
    --c-bg-input: #12171c;
    --c-bg-sidebar: #0a0a0a;
    --c-bg-sidebar-hover: #1a1a1a;

    --c-border: #2b333c;
    --c-border-focus: #FBBF24;

    --c-gold: #FBBF24;
    --c-gold-text: #F4C14B;
    --c-gold-light: #FCD34D;
    --c-gold-dim: #D4A017;
    --c-gold-bg: rgba(251, 191, 36, 0.14);

    --c-text: #E6E9ED;
    --c-text-secondary: #C0C7CF;
    --c-text-muted: #98A2AD;
    --c-text-dim: #98A2AD;

    --c-side-text: #999999;
    --c-side-text-dim: #8a8a8a;
    --c-side-text-active: #FBBF24;

    --c-success: #4ADE80;
    --c-success-bg: rgba(74, 222, 128, 0.12);
    --c-success-light: #10231a;
    --c-danger: #F87171;
    --c-danger-bg: rgba(248, 113, 113, 0.12);
    --c-danger-light: #2a1416;
    --c-warn: #FBBF24;
    --c-warn-bg: rgba(251, 191, 36, 0.12);
    --c-warn-light: #2a2109;
    --c-info: #8AB4FF;
    --c-info-bg: rgba(138, 180, 255, 0.12);
    --c-info-light: #101b2e;

    --c-special: #C4A7F5;
    --c-special-bg: rgba(196, 167, 245, 0.12);
    --c-neutral: #A9B4C0;
    --c-neutral-bg: rgba(169, 180, 192, 0.12);

    --c-on-danger: #111111;
    --c-on-success: #111111;

    --c-white: #FFFFFF;
    --c-black: #111111;
  }
}

:root[data-theme="dark"] {
  --c-bg: #101418;
  --c-bg-card: #181d23;
  --c-bg-card-hover: #1f252c;
  --c-bg-input: #12171c;
  --c-bg-sidebar: #0a0a0a;
  --c-bg-sidebar-hover: #1a1a1a;

  --c-border: #2b333c;
  --c-border-focus: #FBBF24;

  --c-gold: #FBBF24;
  --c-gold-text: #F4C14B;
  --c-gold-light: #FCD34D;
  --c-gold-dim: #D4A017;
  --c-gold-bg: rgba(251, 191, 36, 0.14);

  --c-text: #E6E9ED;
  --c-text-secondary: #C0C7CF;
  --c-text-muted: #98A2AD;
  --c-text-dim: #98A2AD;

  --c-side-text: #999999;
  --c-side-text-dim: #8a8a8a;
  --c-side-text-active: #FBBF24;

  --c-success: #4ADE80;
  --c-success-bg: rgba(74, 222, 128, 0.12);
  --c-success-light: #10231a;
  --c-danger: #F87171;
  --c-danger-bg: rgba(248, 113, 113, 0.12);
  --c-danger-light: #2a1416;
  --c-warn: #FBBF24;
  --c-warn-bg: rgba(251, 191, 36, 0.12);
  --c-warn-light: #2a2109;
  --c-info: #8AB4FF;
  --c-info-bg: rgba(138, 180, 255, 0.12);
  --c-info-light: #101b2e;

  --c-special: #C4A7F5;
  --c-special-bg: rgba(196, 167, 245, 0.12);
  --c-neutral: #A9B4C0;
  --c-neutral-bg: rgba(169, 180, 192, 0.12);

  --c-on-danger: #111111;
  --c-on-success: #111111;

  --c-white: #FFFFFF;
  --c-black: #111111;
}

:root[data-density="compact"] {
  --density-cell-pad-y: 5px;
  --density-cell-pad-x: 8px;
  --density-row-font-size: 12px;
}

/* The app shell paints `--c-bg` on its own root <div>, but that div does not
 * cover the overscroll band or the area behind a short page, so the UA's
 * white would show through the dark theme at the edges. */
html,
body {
  background: var(--c-bg);
  color: var(--c-text);
}

/* M-11: visible keyboard focus indicator. Color must stay in lockstep with
 * C.borderFocus in src/lib/theme.ts — enforced by src/lib/focus-styles.test.ts. */
:focus-visible {
  outline: 2px solid var(--c-border-focus); /* theme.ts C.borderFocus */
  outline-offset: 2px;
}

/* The gold outline is invisible against the gold active-tab/sidebar accent
 * backgrounds only at 1px; 2px offset keeps it readable on white and dark. */

/* H-4: backdrop dim for the shared <Dialog> (native <dialog>/showModal()).
 * Matches the rgba(0,0,0,0.35) overlay the legacy modal divs used. */
dialog::backdrop {
  background: rgba(0, 0, 0, 0.35);
}
